/**
 * Temp_ExcelPreview — 업로드한 엑셀(.xlsx)을 인쇄 미리보기처럼 보여 주는 임시 컴포넌트 (D-07 §3.5)
 *
 * 표준에 문서 뷰어가 없어 임시 컴포넌트로 만든다. 승격·치환은 UI팀이 판단한다.
 * - ExcelJS 를 이 화면을 열 때만 불러온다(동적 import) — 다른 화면의 번들에 들어가지 않는다
 * - 시트의 병합·열 너비·행 높이·채우기·테두리·글꼴·정렬·표시형식을 읽어 흰 종이 위에 그대로 그린다(Temp_excelSheetModel)
 * - 기본은 "맞춤"(패널 폭에 맞춰 축소). 막대(− ＋ 맞춤)와 Ctrl+휠로 확대하면 가로·세로 스크롤 · 마우스로 끌어서 이동(Temp_zoom)
 * - 읽지 못하는 파일(손상·암호·너무 큼)은 fallback 을 보여 준다
 *
 * 셀의 색·크기·테두리는 파일이 가진 값(데이터)이라 인라인 스타일로 적용한다. 토큰으로 바꿀 수 없다.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Workbook } from 'exceljs'
import { IconInfoCircle, Skeleton, StatusHint, Tabs } from '@indipia/ui-core'
import { buildSheetModel, SHEET_PAPER, type SheetEdge, type SheetModel } from './Temp_excelSheetModel'
import { stepZoom, Temp_ZoomBar, Temp_ZoomHint, useCtrlWheelZoom, useDragPan, useZoomAnchor } from './Temp_zoom'

/** 이보다 큰 파일은 화면을 멈추지 않도록 읽지 않고 내려받기로 안내한다 */
const MAX_BYTES = 15 * 1024 * 1024

type Loaded =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; wb: Workbook; names: string[] }

const edgeCss = (e?: SheetEdge) => (e ? `${e.width}px ${e.style} ${e.color}` : undefined)

export function Temp_ExcelPreview({ file, fallback }: { file: File; fallback: ReactNode }) {
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' })
  const [sheetName, setSheetName] = useState('')
  // 'fit' = 패널 폭에 맞춤, 숫자 = 실제 크기(1)에 대한 배율
  const [zoom, setZoom] = useState<'fit' | number>('fit')
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    let alive = true
    setLoaded({ status: 'loading' })
    if (file.size > MAX_BYTES) { setLoaded({ status: 'error' }); return }
    void (async () => {
      try {
        const ns = await import('exceljs')
        // 브라우저용 번들은 default 로, 노드용은 이름 있는 export 로 내려온다 — 둘 다 받는다
        const Wb = ns.Workbook ?? (ns as unknown as { default: typeof ns }).default.Workbook
        const wb = new Wb()
        await wb.xlsx.load(await file.arrayBuffer())
        const names = wb.worksheets.filter(ws => ws.state === 'visible').map(ws => ws.name)
        if (!alive) return
        if (names.length === 0) throw new Error('보이는 시트가 없다')
        setSheetName(names[0])
        setLoaded({ status: 'ready', wb, names })
      } catch {
        if (alive) setLoaded({ status: 'error' })
      }
    })()
    return () => { alive = false }
  }, [file])

  const built = useMemo<SheetModel | 'fail' | null>(() => {
    if (loaded.status !== 'ready') return null
    const ws = loaded.wb.getWorksheet(sheetName)
    if (!ws) return null
    try { return buildSheetModel(ws, loaded.wb) } catch { return 'fail' }
  }, [loaded, sheetName])
  const model = built && built !== 'fail' ? built : null

  // 시트에 들어 있는 그림(도장·로고)의 임시 주소
  const [imgUrls, setImgUrls] = useState<string[]>([])
  useEffect(() => {
    if (!model || model.images.length === 0) { setImgUrls([]); return }
    const urls = model.images.map(im => URL.createObjectURL(new Blob([im.data as BlobPart], { type: im.mime })))
    setImgUrls(urls)
    return () => urls.forEach(u => URL.revokeObjectURL(u))
  }, [model])

  // 너비 맞춤 — 종이 안쪽 너비를 재서 시트 너비가 더 크면 그만큼 줄인다(키우지는 않는다)
  const measureRef = useRef<HTMLDivElement>(null)
  const [avail, setAvail] = useState(0)
  useEffect(() => {
    const el = measureRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => setAvail(entries[0].contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [model])

  // 배율 — 맞춤은 시트 너비가 더 클 때만 줄이고(키우지 않는다), 막대·Ctrl+휠은 맞춤 배율에서 한 단계씩 움직인다
  const fitScale = model && avail > 0 && model.width > 0 ? Math.min(1, avail / model.width) : 1
  const scale = zoom === 'fit' ? fitScale : zoom
  const step = (dir: 1 | -1) => setZoom(stepZoom(scale, dir))
  useCtrlWheelZoom(scrollEl, step)
  useDragPan(scrollEl)
  useZoomAnchor(scrollEl, scale)

  if (loaded.status === 'error' || built === 'fail') return <>{fallback}</>
  if (loaded.status === 'loading' || !model) {
    return (
      <div className="flex flex-col gap-03 p-04" role="status" aria-busy="true" aria-label="엑셀 미리보기를 준비하는 중">
        <Skeleton className="h-03" />
        <Skeleton className="h-03" />
        <Skeleton className="h-03" />
      </div>
    )
  }

  const names = loaded.names

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-02 border-b border-border px-04 py-02">
        {names.length > 1 ? (
          <div className="min-w-0 overflow-x-auto">
            <Tabs variant="pill" size="sm" aria-label="시트 선택" tabs={names.map(n => ({ label: n, value: n }))} value={sheetName} onChange={setSheetName} />
          </div>
        ) : (
          <span className="min-w-0 truncate text-caption text-text-muted" title={sheetName}>{sheetName}</span>
        )}
        <Temp_ZoomBar scale={scale} fitted={zoom === 'fit'} onStep={step} onFit={() => setZoom('fit')} />
      </div>

      <Temp_ZoomHint />

      {model.clipped && (
        <div className="shrink-0 px-04 pb-02">
          <StatusHint tone="muted" icon={<IconInfoCircle width={16} height={16} />}>큰 시트라 앞부분만 보여요. 전체는 원본을 내려받아 확인해 주세요</StatusHint>
        </div>
      )}

      {/* 스크롤 영역 — 세로 스크롤바를 항상 둬서 맞춤 계산이 스크롤바 유무로 흔들리지 않게 한다 */}
      <div ref={setScrollEl} className="min-h-0 flex-1 overflow-x-auto overflow-y-scroll bg-surface p-04">
        {/* 맞춤 폭 측정용 — 종이와 같은 여백을 가진 보이지 않는 틀. 확대 중에 종이가 넓어져도 영향받지 않고 늘 "종이 안쪽에 들어갈 수 있는 폭"을 잰다 */}
        <div className="h-0 overflow-hidden" aria-hidden="true">
          <div className="invisible border border-border p-04"><div ref={measureRef} className="h-01" /></div>
        </div>
        <div
          className={zoom === 'fit' ? 'border border-border p-04' : 'w-max min-w-full border border-border p-04'}
          style={{ backgroundColor: SHEET_PAPER }}
          role="group"
          aria-label={`${sheetName} 미리보기`}
        >
          <div>
            <div style={{ width: model.width * scale, height: model.height * scale, visibility: zoom === 'fit' && avail === 0 ? 'hidden' : undefined }}>
              <div
                className="relative grid"
                style={{
                  width: model.width,
                  height: model.height,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  gridTemplateColumns: model.colWidths.map(w => `${w}px`).join(' '),
                  gridTemplateRows: model.rowHeights.map(h => `${h}px`).join(' '),
                }}
              >
                {model.boxes.map(b => (
                  <div
                    key={b.key}
                    className="box-border flex flex-col px-01"
                    style={{
                      gridColumn: `${b.col + 1} / span ${b.colSpan}`,
                      gridRow: `${b.row + 1} / span ${b.rowSpan}`,
                      backgroundColor: b.fill,
                      color: b.color,
                      fontFamily: b.font,
                      fontSize: b.fontSize,
                      fontWeight: b.bold ? 700 : 400,
                      fontStyle: b.italic ? 'italic' : undefined,
                      textDecoration: [b.underline && 'underline', b.strike && 'line-through'].filter(Boolean).join(' ') || undefined,
                      justifyContent: b.valign,
                      overflow: b.spill ? 'visible' : 'hidden',
                      borderTop: edgeCss(b.top),
                      borderLeft: edgeCss(b.left),
                      borderRight: edgeCss(b.right),
                      borderBottom: edgeCss(b.bottom),
                      paddingLeft: b.align === 'left' && b.indent > 0 ? `calc(var(--spacing-01) + ${b.indent * 9}px)` : undefined,
                      paddingRight: b.align === 'right' && b.indent > 0 ? `calc(var(--spacing-01) + ${b.indent * 9}px)` : undefined,
                    }}
                  >
                    {b.text && (
                      <span className="block leading-tight" style={{ textAlign: b.align, whiteSpace: b.wrap ? 'pre-wrap' : 'pre', overflowWrap: 'anywhere' }}>
                        {b.text}
                      </span>
                    )}
                  </div>
                ))}
                {model.images.map((im, i) => imgUrls[i] && (
                  <img key={i} src={imgUrls[i]} alt="" className="absolute max-w-none" style={{ left: im.left, top: im.top, width: im.width, height: im.height }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
